import React from 'react'

import {
  useAccount,
  useActions,
  useAppcues,
  useCallback,
  useDebounce,
  useMeasurements,
  useMemo,
  useNavigate,
  useQueries,
  useState,
} from 'src/hooks'
import { report } from 'src/utils/error'
import { ACCOUNT_TOOLS_ID, RP_TOOLS_IDS } from 'src/app/constants'
import {
  CampaignFragment,
  getItemCategories,
  ItemCategoryFragment,
  ItemFragment,
  PlanFragment,
} from 'src/graphql'
import {
  DefaultError,
  Drawer,
  FullWindowModal,
  InstantSearch,
  SuspenseBoundary,
  Transition,
} from 'src/chrome'
import Plans from 'src/plans/containers/Plans'
import PlanBuyer from 'src/plan-buyer/containers/PlanBuyer'

import {
  Banner,
  CampaignList,
  Item as ItemComponent,
  PaymentModal,
  ThanksModal,
  TileRibbon,
} from '../components'
import SearchResults from './SearchResults'
import styles from './campaignStore.module.scss'
import { CampaignStoreRoute } from '../routes/CampaignStoreRoute'
import { Item } from '../types'
import CircularProgress from '@material-ui/core/CircularProgress'

const upsaleMessage = {
  title: "Sorry, you can't purchase campaigns on a basic subscription",
  message:
    'Campaigns are an amazing and easy way to help your customers feel appreciation. Campaigns can be used to set a sequence of cards to send based on time delays. Never forget to send an amazing follow-up card again and upgrade your account today!',
}

interface Props {
  route: CampaignStoreRoute
}

enum Steps {
  Idle,
  Preview,
  ChoosePlan,
  BuyPlan,
  BuyItem,
  Thanks,
}

type ItemType = 'campaign' | 'product'

interface ItemBuy {
  itemType: ItemType
  item: ItemFragment
  plan: PlanFragment
}

type CampaignBuy = ItemBuy & {
  itemType: 'campaign'
  item: ItemFragment & { campaign: CampaignFragment }
}

type ProductBuy = ItemBuy & {
  itemType: 'product'
  item: ItemFragment & { campaign: null }
}

type IdleStep = { type: Steps.Idle }
type PreviewStep = { type: Steps.Preview; itemId: string }
type ChoosePlanStep = { type: Steps.ChoosePlan; itemId: string }
type ThanksStep = { type: Steps.Thanks }
type BuyPlanStep = { type: Steps.BuyPlan; itemId: string; planName: string }
type BuyItemStep = {
  type: Steps.BuyItem
} & (CampaignBuy | ProductBuy)

type Step =
  | IdleStep
  | PreviewStep
  | ChoosePlanStep
  | BuyItemStep
  | BuyPlanStep
  | ThanksStep

const featuredProducts: (Item & { startColor: string; endColor: string })[] = [
  {
    __typename: 'Item',
    id: '562',
    campaign: null,
    itemCode: '13704',
    name: 'The Essentials Pack',
    description: [
      'The essentials pack includes a personalized handwriting font,',
      'four signatures, and 6 beautifully designed first impressions cards',
    ].join(' '),
    price: '$99',
    image:
      'https://www.sendoutcards.com/soc-media/images/EssentialsPack.original.jpg',
    startColor: 'rgb(255, 219, 48)',
    endColor: 'rgb(244, 140, 67)',
  },
]

const Hooks = (props: Props) => {
  const {
    route: { categoryId },
  } = props

  const { CampaignStoreIndex, CampaignStoreCategory, push } = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearch = useDebounce(searchTerm, 500)
  const actions = useActions()
  const [action, setStep] = useState<Step>({ type: Steps.Idle })
  const account = useAccount()
  const { windowWidth } = useMeasurements()

  const endStep = () => setStep({ type: Steps.Idle })

  const [allCategories] = useQueries(getItemCategories())

  const selectedCategory: ItemCategoryFragment | undefined = useMemo(() => {
    if (categoryId) {
      const category = allCategories.find(x => x.id === categoryId)
      if (category) {
        return category
      }

      report(new Error(`campaign category ${categoryId} not found`))
      push(CampaignStoreIndex, {})
    }

    return undefined
  }, [allCategories, categoryId, CampaignStoreIndex, push])

  /* An array of all the categories appearing on the page. If a category is
     selected display only that, otherwise show non-emptyish ones. */
  const displayedCategories = useMemo(() => {
    if (selectedCategory) {
      return [selectedCategory]
    }

    return allCategories
      .filter(category => category.items.length > 0)
      .filter(category => !RP_TOOLS_IDS.includes(category.id))
      .filter(x => x.items.some(item => item.campaign || item.image))
  }, [allCategories, selectedCategory])

  /* Split categories as the headerCategory is displayed first on top,
     followed by a horizontal Ribbon, followed by otherCategories. */
  const headerCategory = useMemo(
    () => displayedCategories.find(x => x.id === ACCOUNT_TOOLS_ID),
    [displayedCategories],
  )
  const otherCategories = useMemo(
    () => displayedCategories.filter(x => x.id !== ACCOUNT_TOOLS_ID),
    [displayedCategories],
  )

  const onSelectCategory = (id: string | undefined) => {
    id
      ? push(CampaignStoreCategory, { categoryId: id })
      : push(CampaignStoreIndex, undefined)
  }

  const getItem = useCallback(
    (id: string) => {
      for (const category of allCategories) {
        for (const item of category.items) {
          if (item.id === id) {
            return item
          }
        }
      }

      throw new Error(`item not found: ${id}`)
    },
    [allCategories],
  )

  const onPreview = (itemId: string | undefined) =>
    itemId ? setStep({ type: Steps.Preview, itemId }) : endStep()

  const onPurchase = useCallback(
    (itemId: string) => {
      const item = getItem(itemId)
      const { campaign } = item

      // TS cannot narrow down object properties even if we test
      // them. To maintain type safety we assign the properties we want to
      // narrow (campaign) to variables and pass those variables
      // explicitly when building the final object.

      if (campaign) {
        // Update (1/20/21): Basic accounts can access/purchase campaigns.
        // account has a plan, allow buying the item
        setStep({
          type: Steps.BuyItem,
          item: {
            ...item,
            campaign,
          },
          itemType: 'campaign',
          plan: { ...account.plan },
        })
      } else {
        // purchase an item
        setStep({
          type: Steps.BuyItem,
          itemType: 'product',
          item: { ...item, campaign },
          plan: account.plan,
        })
      }
    },
    [getItem, account],
  )

  const onSelectPlan = (planName: string, itemId: string) => {
    setStep({
      type: Steps.BuyPlan,
      itemId: itemId,
      planName,
    })
  }

  return {
    action,
    actions,
    debouncedSearch,
    setStep,
    endStep,
    searchTerm,
    setSearchTerm,
    getItem,
    onSelectCategory,
    selectedCategory,
    onSelectPlan,
    onPreview,
    onPurchase,
    headerCategory,
    otherCategories,
    account,
    windowWidth,
  }
}

const CampaignStoreContent: React.FC<Props> = props => {
  const {
    route: { categoryId },
  } = props

  const hooks = Hooks(props)
  const { action } = hooks

  const isShowingSingleCategory = categoryId !== undefined
  const isScrollBarEnabled =
    action.type !== Steps.Preview && action.type !== Steps.BuyItem

  useAppcues('-L8DaP3XvZmu5XZ88mgo')

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {/* If a category is selected, we show the banner in "category" mode,
             which displays a title and a back button. Otherwise, we show a
             promotion using the hardcoded featuredProducts. TODO: split Banner
             into two different components */}
        {hooks.selectedCategory ? (
          <Banner
            isCategorySelected={true}
            title={hooks.selectedCategory.name}
            textColor={'white'}
            backgroundStartColor={'#6db0ff'}
            backgroundEndColor={'#7de8f6'}
            onPurchase={product => hooks.onPurchase(product.id)}
            onClose={() => hooks.onSelectCategory(undefined)}
          />
        ) : (
          featuredProducts.map(product => (
            <Banner
              isCategorySelected={false}
              key={product.id}
              image={product.image ?? undefined}
              title={product.name ?? ''}
              product={product}
              textColor={'white'}
              backgroundStartColor={product.startColor}
              backgroundEndColor={product.endColor}
              onPurchase={() => hooks.onPurchase(product.id)}
            />
          ))
        )}
      </div>

      <InstantSearch
        id={'campaign_search_bar'}
        onSearch={hooks.setSearchTerm}
        autocomplete={'off'}
      />

      {/* If a search is in progress, show the results. Otherwise show categories */}
      {hooks.debouncedSearch ? (
        <SuspenseBoundary
          unresolved={
            <CircularProgress
              style={{
                marginLeft: 'calc(50% - 20px)',
              }}
            />
          }
          failure={DefaultError}
        >
          <SearchResults
            searchTerm={hooks.debouncedSearch}
            categoryId={categoryId}
            isScrollBarEnabled={isScrollBarEnabled}
            onPreview={hooks.onPreview}
            onPurchase={hooks.onPurchase}
          />
        </SuspenseBoundary>
      ) : (
        <>
          {hooks.headerCategory && (
            <CampaignList
              key={hooks.headerCategory.id}
              category={hooks.headerCategory}
              isScrollBarEnabled={isScrollBarEnabled}
              onPreview={hooks.onPreview}
              onPurchase={hooks.onPurchase}
              isSeeAllButtonEnabled={!isShowingSingleCategory}
              isWrapContentEnabled={isShowingSingleCategory}
              onSelectCategory={hooks.onSelectCategory}
            />
          )}

          {hooks.otherCategories.length && !hooks.selectedCategory ? (
            <TileRibbon
              items={hooks.otherCategories}
              isScrollBarEnabled={isScrollBarEnabled}
              title={''}
              isSeeAllButtonEnabled={false}
              isWrapContentEnabled={false}
              onItemCategoryClick={category =>
                hooks.onSelectCategory(category.id)
              }
            />
          ) : null}

          {hooks.otherCategories.map(category => (
            <CampaignList
              key={category.id}
              category={category}
              isScrollBarEnabled={isScrollBarEnabled}
              onPreview={hooks.onPreview}
              onPurchase={hooks.onPurchase}
              isSeeAllButtonEnabled={!isShowingSingleCategory}
              isWrapContentEnabled={isShowingSingleCategory}
              onSelectCategory={hooks.onSelectCategory}
            />
          ))}
        </>
      )}

      {/* We're viewing an item, show the modal. */}
      {action.type === Steps.Preview && (
        <SuspenseBoundary unresolved={''} failure={DefaultError}>
          <ItemComponent
            onPurchase={hooks.onPurchase}
            item={hooks.getItem(action.itemId)}
            onClose={hooks.endStep}
          />
        </SuspenseBoundary>
      )}

      {/* User required a plan upgrade, show plan selector */}
      {action.type === Steps.ChoosePlan && (
        <SuspenseBoundary unresolved={''} failure={DefaultError}>
          <FullWindowModal close={hooks.endStep}>
            <Plans
              title={upsaleMessage.title}
              message={upsaleMessage.message}
              onSelect={name => hooks.onSelectPlan(name, action.itemId)}
            />
          </FullWindowModal>
        </SuspenseBoundary>
      )}

      {/* User has picked a plan, show buying modal */}
      {action.type === Steps.BuyPlan && (
        <SuspenseBoundary unresolved={''} failure={DefaultError}>
          <Drawer
            openCloseControlClicked={hooks.endStep}
            isOpen={true}
            title={'Purchase Your Subscription Package'}
            isFullScreen={hooks.windowWidth < 1060}
          >
            <PlanBuyer
              currentPlan={hooks.account.plan.title}
              initialPlanName={action.planName}
              // plan has been bought, re-attempt the purchasing process
              onAfterPurchase={() => hooks.onPurchase(action.itemId)}
              onCancel={hooks.endStep}
            />
          </Drawer>
        </SuspenseBoundary>
      )}

      {action.type === Steps.BuyItem && (
        <PaymentModal
          account={hooks.account}
          item={action.item}
          onCancel={hooks.endStep}
          onAfterPurchase={() => hooks.setStep({ type: Steps.Thanks })}
        />
      )}

      {action.type === Steps.Thanks && <ThanksModal onClose={hooks.endStep} />}
    </div>
  )
}

const CampaignStore: React.FC<Props> = props => (
  <SuspenseBoundary
    unresolved={<Transition message={'Gathering campaigns...'} />}
    failure={DefaultError}
  >
    <CampaignStoreContent {...props} />
  </SuspenseBoundary>
)

export default CampaignStore
